import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  CheckCircle2, 
  AlertTriangle,
  Download,
  Filter,
  Search,
  BarChart3,
  TrendingUp,
  Archive,
} from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { useComplianceMetrics, useAuditEvents } from '../../../hooks/useSecurity'
import type { AuditCategory } from '../../../types/security'
// import { AuditEvent, ComplianceStandard } from '@/types/security';
import { cn } from '../../../utils/cn'
import { format, formatRelativeTime } from '../../../utils/format'

// Helper function to convert time range string to actual time range
function getTimeRange(range: string): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  
  switch (range) {
    case '1h':
      start.setHours(start.getHours() - 1);
      break;
    case '24h':
      start.setHours(start.getHours() - 24);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    default:
      start.setHours(start.getHours() - 24);
  }
  
  return { start, end };
}

export const SecurityAuditDashboard: React.FC = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [categoryFilter, setCategoryFilter] = useState<AuditCategory[]>([]);
  const [riskLevelFilter, setRiskLevelFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  const { data: complianceMetrics, isLoading: metricsLoading } = useComplianceMetrics();
  
  // Create stable time range with ISO string dates to prevent re-renders
  const timeRange = useMemo(() => {
    const range = getTimeRange(selectedTimeRange);
    return {
      start: range.start.toISOString(),
      end: range.end.toISOString(),
    };
  }, [selectedTimeRange]);
  
  const { data: auditEvents, isLoading: _eventsLoading } = useAuditEvents({
    category: categoryFilter.length > 0 ? categoryFilter : undefined,
    riskLevel: riskLevelFilter.length > 0 ? riskLevelFilter : undefined,
    limit: 100,
    timeRange,
  });

  const filteredEvents = useMemo(() => {
    if (!auditEvents) {
      return [];
    }
    
    let filtered = auditEvents;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((event: any) =>
        event.action.toLowerCase().includes(search) ||
        event.actor?.name?.toLowerCase().includes(search) ||
        event.actor?.id?.toLowerCase().includes(search) ||
        event.nodeId.toLowerCase().includes(search) ||
        event.target?.name?.toLowerCase().includes(search),
      );
    }
    
    return filtered;
  }, [auditEvents, searchTerm]);

  const auditStats = useMemo(() => {
    if (!auditEvents) {
      return { byCategory: {}, byRiskLevel: {}, byResult: {}, byActor: {} };
    }
    
    const byCategory = auditEvents.reduce((acc: Record<string, number>, event: any) => {
      acc[event.category] = (acc[event.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byRiskLevel = auditEvents.reduce((acc: Record<string, number>, event: any) => {
      acc[event.riskLevel] = (acc[event.riskLevel] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byResult = auditEvents.reduce((acc: Record<string, number>, event: any) => {
      acc[event.result] = (acc[event.result] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byActor = auditEvents.reduce((acc: Record<string, number>, event: any) => {
      if (event.actor && event.actor.type) {
        acc[event.actor.type] = (acc[event.actor.type] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    
    return { byCategory, byRiskLevel, byResult, byActor };
  }, [auditEvents]);

  const criticalEvents = useMemo(() =>
    filteredEvents?.filter((event: any) => event.riskLevel === 'critical') || [],
    [filteredEvents],
  );

  const failedEvents = useMemo(() =>
    filteredEvents?.filter((event: any) => event.result === 'failure' || event.result === 'error') || [],
    [filteredEvents],
  );

  const handleExportAuditLog = () => {
    console.log('Exporting audit log...');
  };

  const handleViewEventDetails = (eventId: string) => {
    setSelectedEvent(selectedEvent === eventId ? null : eventId);
  };

  if (metricsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-600 rounded-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-console-text">Security Audit & Compliance</h1>
            <p className="text-console-muted">Comprehensive audit logging and compliance monitoring</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={handleExportAuditLog}>
            <Download className="w-4 h-4 mr-2" />
            Export Audit Log
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Compliance Score</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceMetrics?.overview.complianceScore?.toFixed(1) || '0'}%
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp className="w-3 h-3 text-status-online" />
                <span className="text-xs text-status-online">+2.3% this month</span>
              </div>
            </div>
            <div className="p-3 bg-status-online/20 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-status-online" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Audit Events</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceMetrics?.overview.auditEvents?.toLocaleString() || '0'}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {criticalEvents.length} critical
              </p>
            </div>
            <div className="p-3 bg-primary-600/20 rounded-lg">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Policy Violations</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceMetrics?.overview.violations || 0}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {failedEvents.length} recent failures
              </p>
            </div>
            <div className="p-3 bg-danger-600/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Retention Compliance</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceMetrics?.overview.retentionCompliance?.toFixed(1) || '0'}%
              </p>
              <p className="text-xs text-console-muted mt-1">
                Data retention policies
              </p>
            </div>
            <div className="p-3 bg-secondary-600/20 rounded-lg">
              <Archive className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Compliance Standards */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-console-text mb-4">Compliance Standards</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(complianceMetrics?.standards ?? {}).map(([standardId, standard]: [string, any]) => (
            <div
              key={standardId}
              className={cn(
                'p-4 border rounded-lg',
                standard.status === 'compliant' ? 'border-status-online bg-status-online/10' :
                standard.status === 'non_compliant' ? 'border-danger-600 bg-danger-600/10' :
                standard.status === 'partial' ? 'border-warning-600 bg-warning-600/10' :
                'border-console-border bg-console-surface/30',
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-console-text">{standard.name}</h4>
                <Badge variant="severity" severity={getComplianceSeverity(standard.status)}>
                  {standard.status.replace('_', ' ')}
                </Badge>
              </div>
              
              <div className="text-2xl font-bold text-console-text mb-1">
                {standard.score.toFixed(1)}%
              </div>
              
              <div className="text-sm text-console-muted mb-3">
                Requirements: {standard.requirements.length}
              </div>
              
              <div className="flex items-center justify-between text-xs text-console-muted">
                <span>Last: {formatRelativeTime(standard.lastAssessment)}</span>
                <span>Next: {formatRelativeTime(standard.nextAssessment)}</span>
              </div>
            </div>
          )) || []}
        </div>
      </Card>

      {/* Audit Timeline */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-console-text">Audit Timeline</h3>
          <div className="flex items-center gap-2">
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="bg-console-surface border border-console-border rounded px-3 py-1 text-sm text-console-text"
            >
              <option value="1h">1 hour</option>
              <option value="6h">6 hours</option>
              <option value="24h">24 hours</option>
              <option value="7d">7 days</option>
            </select>
          </div>
        </div>
        
        {/* Chart placeholder */}
        <div className="h-48 bg-console-surface/30 rounded-lg border border-console-border flex items-center justify-center">
          <div className="text-center">
            <BarChart3 className="w-8 h-8 text-console-muted mx-auto mb-2" />
            <p className="text-console-muted">Audit event timeline</p>
            <p className="text-xs text-console-muted mt-1">
              Events by category, risk level, and compliance violations
            </p>
          </div>
        </div>
      </Card>

      {/* Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-console-text mb-4">Event Categories</h3>
          <div className="space-y-3">
            {Object.entries(auditStats.byCategory).map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    getCategoryColor(category),
                  )} />
                  <span className="text-sm text-console-text capitalize">
                    {category.replace('_', ' ')}
                  </span>
                </div>
                <span className="text-sm font-medium text-console-text">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold text-console-text mb-4">Risk Levels</h3>
          <div className="space-y-3">
            {Object.entries(auditStats.byRiskLevel).map(([risk, count]) => (
              <div key={risk} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    getRiskLevelColor(risk),
                  )} />
                  <span className="text-sm text-console-text capitalize">{risk}</span>
                </div>
                <span className="text-sm font-medium text-console-text">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold text-console-text mb-4">Actor Types</h3>
          <div className="space-y-3">
            {Object.entries(auditStats.byActor).map(([actor, count]) => (
              <div key={actor} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    getActorTypeColor(actor),
                  )} />
                  <span className="text-sm text-console-text capitalize">{actor}</span>
                </div>
                <span className="text-sm font-medium text-console-text">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted" />
            <input
              type="text"
              placeholder="Search audit events by action, actor, target..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-console-surface border border-console-border rounded-lg text-console-text placeholder-console-muted focus:outline-none focus:ring-2 focus:ring-primary-600"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-console-muted" />
          <select
            multiple
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(Array.from(e.target.selectedOptions, option => option.value as AuditCategory))}
            className="bg-console-surface border border-console-border rounded px-3 py-2 text-sm text-console-text"
          >
            <option value="authentication">Authentication</option>
            <option value="authorization">Authorization</option>
            <option value="data_access">Data Access</option>
            <option value="data_modification">Data Modification</option>
            <option value="system_administration">System Admin</option>
            <option value="configuration_change">Config Change</option>
            <option value="user_management">Management</option>
            <option value="key_management">Key Management</option>
            <option value="network_activity">Network </option>
            <option value="consensus_operation">Consensus</option>
          </select>
          
          <select
            multiple
            value={riskLevelFilter}
            onChange={(e) => setRiskLevelFilter(Array.from(e.target.selectedOptions, option => option.value))}
            className="bg-console-surface border border-console-border rounded px-3 py-2 text-sm text-console-text"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>

      {/* Recent Audit Events */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-console-text">Recent Audit Events</h3>
          <div className="flex items-center gap-2">
            <Badge variant="severity" severity={criticalEvents.length > 0 ? 'critical' : 'info'}>
              {criticalEvents.length} critical
            </Badge>
            <Badge variant="severity" severity={failedEvents.length > 0 ? 'warning' : 'info'}>
              {failedEvents.length} failed
            </Badge>
          </div>
        </div>
        
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredEvents?.slice(0, 50).map((event: any) => (
            <div
              key={event.id}
              className={cn(
                'p-3 rounded-lg border cursor-pointer transition-colors',
                event.riskLevel === 'critical' || event.result === 'failure' || event.result === 'error'
                  ? 'border-danger-600/50 bg-danger-600/10'
                  : event.riskLevel === 'high'
                  ? 'border-warning-600/50 bg-warning-600/10'
                  : 'border-console-border bg-console-surface/30 hover:bg-console-surface/50',
                selectedEvent === event.id && 'border-primary-600 bg-primary-600/10',
              )}
              onClick={() => handleViewEventDetails(event.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {event.category && (
                    <Badge variant="severity" severity={getCategorySeverity(event.category)}>
                      {event.category.replace('_', ' ')}
                    </Badge>
                  )}
                  {event.result && (
                    <Badge variant="severity" severity={getResultSeverity(event.result)}>
                      {event.result}
                    </Badge>
                  )}
                  {event.riskLevel && (
                    <Badge variant="severity" severity={getRiskLevelSeverity(event.riskLevel)}>
                      {event.riskLevel}
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-console-muted">
                  {formatRelativeTime(event.timestamp)}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div>
                  <span className="text-console-muted">Action: </span>
                  <span className="text-console-text font-medium">{event.action || 'Unknown'}</span>
                </div>
                
                <div>
                  <span className="text-console-muted">Actor: </span>
                  <span className="text-console-text">
                    {event.actor ? `${event.actor.name || event.actor.id} (${event.actor.type})` : 'Unknown'}
                  </span>
                </div>
                
                <div>
                  <span className="text-console-muted">Node: </span>
                  <span className="text-console-text">{event.nodeId || 'N/A'}</span>
                </div>
                
                <div>
                  <span className="text-console-muted">Target: </span>
                  <span className="text-console-text">
                    {event.target?.name || event.target?.id || 'N/A'}
                  </span>
                </div>
              </div>

              {event.actor?.sourceIP && (
                <div className="mt-2 text-xs text-console-muted">
                  Source IP: <code className="bg-console-surface/50 px-1 rounded">{event.actor.sourceIP}</code>
                </div>
              )}

              {selectedEvent === event.id && (
                <div className="mt-3 pt-3 border-t border-console-border">
                  <div className="space-y-2">
                    <div>
                      <span className="text-console-muted text-sm">Session ID: </span>
                      <code className="text-console-text text-sm bg-console-surface/50 px-1 rounded">
                        {event.sessionId || 'N/A'}
                      </code>
                    </div>
                    
                    {event.retention && (
                      <div>
                        <span className="text-console-muted text-sm">Retention: </span>
                        <span className="text-console-text text-sm">
                          Until {format.date(event.retention.retainUntil, 'PPP')} 
                          ({event.retention.reason}, {event.retention.classification})
                        </span>
                      </div>
                    )}
                    
                    {event.details && Object.keys(event.details).length > 0 && (
                      <div>
                        <span className="text-console-muted text-sm">Details: </span>
                        <pre className="text-xs bg-console-surface/50 p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(event.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )) || []}
        </div>
        
        {(!filteredEvents || filteredEvents.length === 0) && (
          <div className="text-center py-8">
            <FileText className="w-8 h-8 text-console-muted mx-auto mb-2" />
            <p className="text-console-muted">No audit events found</p>
          </div>
        )}
      </Card>
    </div>
  );
};

// Helper functions


function getComplianceSeverity(status: string): 'info' | 'warning' | 'error' | 'critical' {
  switch (status) {
    case 'compliant': return 'info';
    case 'non_compliant': return 'error';
    case 'partial': return 'warning';
    default: return 'info';
  }
}

function getCategorySeverity(category: string): 'info' | 'warning' | 'error' | 'critical' {
  switch (category) {
    case 'authentication': return 'info';
    case 'authorization': return 'info';
    case 'data_access': return 'info';
    case 'data_modification': return 'warning';
    case 'system_administration': return 'error';
    default: return 'info';
  }
}

function getResultSeverity(result: string): 'info' | 'warning' | 'error' | 'critical' {
  switch (result) {
    case 'success': return 'info';
    case 'failure': return 'error';
    case 'error': return 'critical';
    default: return 'info';
  }
}

function getRiskLevelSeverity(risk: string): 'info' | 'warning' | 'error' | 'critical' {
  switch (risk) {
    case 'critical': return 'critical';
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'info';
    default: return 'info';
  }
}

function getCategoryColor(category: string): string {
  const colors = {
    authentication: 'bg-primary-600',
    authorization: 'bg-secondary-600',
    data_access: 'bg-status-online',
    data_modification: 'bg-warning-600',
    system_administration: 'bg-danger-600',
    configuration_change: 'bg-purple-600',
    user_management: 'bg-orange-600',
    key_management: 'bg-pink-600',
    network_activity: 'bg-cyan-600',
    consensus_operation: 'bg-teal-600',
  };
  return colors[category as keyof typeof colors] || 'bg-console-muted';
}

function getRiskLevelColor(risk: string): string {
  const colors = {
    critical: 'bg-danger-600',
    high: 'bg-warning-600',
    medium: 'bg-secondary-600',
    low: 'bg-status-online',
  };
  return colors[risk as keyof typeof colors] || 'bg-console-muted';
}

function getActorTypeColor(actor: string): string {
  const colors = {
    user: 'bg-primary-600',
    service: 'bg-secondary-600',
    system: 'bg-warning-600',
    node: 'bg-status-online',
  };
  return colors[actor as keyof typeof colors] || 'bg-console-muted';
}